import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@material-ui/core';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaChevronDown, FaEye, FaPlus, FaTrash } from 'react-icons/fa';
import { LuArrowDown, LuArrowDownUp, LuArrowUp } from "react-icons/lu";
import CustomAddModal from '../../../components/Common/CustomAddModal';
import CustomPagination from '../../../components/Common/CustomPagination';
import CustomShowModal from '../../../components/Common/CustomShowModal';
import { getCurrentUserOrganizationId, toastError, toastSuccess } from '../../../utils';
import { getPostedInvoiceByOrganizationId, updateInvoiceDetails } from './actions';
import AccountingViewModel from './viewModel';


const defaultPaymentEntry = {
  amount: "",
  date: "",
  remarks: "",
  approved: false
};

const getTotalSettledAmt = (entries) =>
  (entries || []).reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);

const PaymentViewModal = ({ data, setPaymentViewData }) => {
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const updateEntriesAndTotal = (itemIndex, nextEntries) => {
    setPaymentViewData(prev => {
      const updatedDetails = prev.data.invoiceDetail.map((item, idx) => {
        if (idx !== itemIndex) return item;
        const totalSettledAmt = getTotalSettledAmt(nextEntries);
        return { ...item, paymentEntries: nextEntries, totalSettledAmt };
      });
      return { ...prev, data: { ...prev.data, invoiceDetail: updatedDetails } };
    });
  };

  const initializePaymentEntries = (itemIndex) => {
    if (!(data.invoiceDetail[itemIndex]?.paymentEntries?.length > 0)) {
      updateEntriesAndTotal(itemIndex, [{ ...defaultPaymentEntry }]);
    }
  };

  const handleAccordionChange = (itemIndex) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? itemIndex : null);
    if (isExpanded) initializePaymentEntries(itemIndex);
  };

  const canAddNewRow = (entries, invoiceTotal) =>
    getTotalSettledAmt(entries) < invoiceTotal;

  const addPaymentRow = (itemIndex, invoiceTotal) => {
    const entries = data.invoiceDetail[itemIndex]?.paymentEntries || [];
    if (!canAddNewRow(entries, invoiceTotal)) return;
    updateEntriesAndTotal(itemIndex, [...entries, { ...defaultPaymentEntry }]);
  };

  const deletePaymentRow = (itemIndex, entryIndex) => {
    const entries = data.invoiceDetail[itemIndex]?.paymentEntries || [];
    if (entries.length <= 1) return;
    const nextEntries = entries.filter((_, i) => i !== entryIndex);
    updateEntriesAndTotal(itemIndex, nextEntries);
  };

  const handlePaymentChange = (itemIndex, entryIndex, field, value, invoiceTotal) => {
    const entries = data.invoiceDetail[itemIndex]?.paymentEntries || [];
    let newValue = value;

    if (field === "amount") {
      const newAmount = parseFloat(value) || 0;
      const otherTotal = entries.reduce((sum, entry, i) =>
        i !== entryIndex ? sum + (parseFloat(entry.amount) || 0) : sum, 0
      );
      if (otherTotal + newAmount > invoiceTotal) {
        newValue = Math.max(invoiceTotal - otherTotal, 0).toFixed(2);
      }
    }

    const nextEntries = entries.map((entry, i) =>
      i === entryIndex
        ? { ...entry, [field]: field === "approved" ? !!value : newValue }
        : entry
    );
    updateEntriesAndTotal(itemIndex, nextEntries);
  };

  if (!data || !data.invoiceDetail) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No payment data available
      </div>
    );
  }
  return (
    <div className="p-1 text-[11px] font-medium scroll-thin">
      <Paper className="p-2 mb-1.5 border border-slate-200 rounded-none bg-slate-50/50" elevation={0}>
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-800 mb-1 pb-0.5 border-b border-slate-200 m-0">Counter Party Details</h3>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <div>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Name</span>
              <p className="text-[11px] text-slate-800 font-medium mt-0.5 m-0">{data.counterParty?.details?.name || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={6} sm={3}>
            <div>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Contact Person</span>
              <p className="text-[11px] text-slate-800 font-medium mt-0.5 m-0">{data.counterParty?.details?.contactPerson || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={6} sm={3}>
            <div>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Email</span>
              <p className="text-[11px] text-slate-800 font-medium mt-0.5 m-0">{data.counterParty?.details?.email || '-'}</p>
            </div>
          </Grid>
          <Grid item xs={6} sm={3}>
            <div>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Phone</span>
              <p className="text-[11px] text-slate-800 font-medium mt-0.5 m-0">{data.counterParty?.details?.phone || '-'}</p>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <div className="mt-1">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-800 mb-1 m-0">
          Invoice Line Items & Payments
        </h3>
        <div className="overflow-x-auto w-full scroll-thin">
          <div className={`transition-all duration-200 ${expandedAccordion !== null ? 'min-w-[500px]' : 'w-full'}`}>
            {data.invoiceDetail.length > 0 ? (
              data.invoiceDetail.map((item, itemIndex) => {
                const entries = item.paymentEntries || [];
                const invoiceTotal = item.totalAmt || 0;
                const totalPaid = item.totalSettledAmt || getTotalSettledAmt(entries);
                const remainingAmount = invoiceTotal - totalPaid;
                const isFullyPaid = remainingAmount <= 0;

                return (
                  <Accordion
                    key={item.invoiceDetail?._id || `item-${itemIndex}`}
                    expanded={expandedAccordion === itemIndex}
                    onChange={handleAccordionChange(itemIndex)}
                    disableGutters
                    square
                    className="!m-0 !mb-1 border border-slate-300 rounded-none overflow-hidden"
                    elevation={0}
                  >
                    <AccordionSummary
                      expandIcon={<FaChevronDown className="text-slate-600" size={12} />}
                      className="bg-slate-100 hover:bg-slate-200 !min-h-[32px] px-2 py-0.5"
                      classes={{ content: '!m-0 !py-0.5' }}
                      style={{ minHeight: "32px" }}
                    >
                      <div className="flex justify-between items-center w-full pr-2">
                        <div className="flex-1">
                          <p className="text-[11px] font-medium text-slate-900 m-0">
                            {item.invoiceDetail?.label || item.label || "Invoice Item"}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-[11px] text-slate-600 line-clamp-1 m-0">
                              {item.invoiceDetail?.description || "-"}
                            </p>
                            {item.isCargo && (
                              <p className="text-[11px] text-sky-600 m-0">
                                ({item.cargoName} • {item.port} • {item.operation})
                              </p>
                            )}
                          </div>
                          {!item.isCargo && item.vesselName && (
                            <p className="text-[11px] text-sky-600 m-0">
                              {item.vesselName}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-3">
                          <p className="text-[11px] font-medium text-slate-900 m-0">
                            {invoiceTotal.toFixed(2)}
                          </p>
                          {totalPaid > 0 && (
                            <>
                              <p className="text-[10px] text-emerald-600 m-0">
                                Paid: {totalPaid.toFixed(2)}
                              </p>
                              <p className={`text-[10px] m-0 ${isFullyPaid ? 'text-emerald-600' : 'text-amber-600'}`}>
                                Bal: {remainingAmount.toFixed(2)}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className="bg-white p-2 text-[11px] font-medium">
                      <div className="w-full">
                        <div>
                          <div className="flex justify-between items-center mb-1.5">
                            <h4 className="text-[11px] font-medium uppercase tracking-wider text-slate-800 m-0">Payment Entries</h4>
                            <Tooltip
                              title={isFullyPaid ? "Invoice amount fully paid" : "Add payment entry"}
                              arrow
                            >
                              <span>
                                <button
                                  type="button"
                                  onClick={() => addPaymentRow(itemIndex, invoiceTotal)}
                                  disabled={isFullyPaid}
                                  className="h-6 px-2 text-[11px] font-medium rounded-none bg-[#313C4C] text-white hover:bg-[#1e2632] transition cursor-pointer flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <FaPlus size={9} />
                                  <span>Add</span>
                                </button>
                              </span>
                            </Tooltip>
                          </div>

                          {isFullyPaid && (
                            <div className="mb-1.5 p-1.5 bg-emerald-50 border border-emerald-200 rounded-none text-center">
                              <p className="text-[11px] text-emerald-700 font-medium m-0">
                                ✓ Invoice fully paid
                              </p>
                            </div>
                          )}

                          {entries.length > 0 ? (
                            <div className="space-y-1">
                              {entries.map((entry, entryIndex) => (
                                <div key={entryIndex} className="flex gap-1.5 items-center">
                                  <div className="flex-1">
                                    <input
                                      type="number"
                                      value={entry.amount}
                                      disabled={entry.approved}
                                      onChange={e =>
                                        handlePaymentChange(itemIndex, entryIndex, "amount", e.target.value, invoiceTotal)
                                      }
                                      placeholder="Amount"
                                      className="w-full h-7 px-2 py-1 border border-slate-300 rounded-none text-[11px] font-medium text-slate-800 focus:outline-none focus:border-[#313C4C] bg-white transition-colors disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <input
                                      type="date"
                                      value={entry.date}
                                      disabled={entry.approved}
                                      onChange={e =>
                                        handlePaymentChange(itemIndex, entryIndex, "date", e.target.value, invoiceTotal)
                                      }
                                      placeholder="Date"
                                      className="w-full h-7 px-2 py-1 border border-slate-300 rounded-none text-[11px] font-medium text-slate-800 focus:outline-none focus:border-[#313C4C] bg-white transition-colors disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <input
                                      type="text"
                                      value={entry.remarks}
                                      disabled={entry.approved}
                                      onChange={e =>
                                        handlePaymentChange(itemIndex, entryIndex, "remarks", e.target.value, invoiceTotal)
                                      }
                                      placeholder="Remarks"
                                      className="w-full h-7 px-2 py-1 border border-slate-300 rounded-none text-[11px] font-medium text-slate-800 focus:outline-none focus:border-[#313C4C] bg-white transition-colors disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                  <div className="shrink-0 flex items-center">
                                    <button
                                      type="button"
                                      onClick={() => deletePaymentRow(itemIndex, entryIndex)}
                                      disabled={entries.length === 1 || entry.approved}
                                      className="h-7 w-7 text-rose-600 hover:bg-rose-50 border border-slate-300 rounded-none flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                      title="Delete entry"
                                    >
                                      <FaTrash size={12} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-2 text-slate-500 text-[11px] font-medium">
                              No payment entries yet. Click "Add" to start.
                            </div>
                          )}

                          {totalPaid > 0 && (
                            <div className="mt-2 p-1.5 bg-slate-50 rounded-none border border-slate-200">
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium m-0">Invoice Amt</p>
                                  <p className="text-[11px] font-medium text-slate-800 mt-0.5 m-0">
                                    {invoiceTotal.toFixed(2)}
                                  </p>
                                </Grid>
                                <Grid item xs={4}>
                                  <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium m-0">Total Paid</p>
                                  <p className="text-[11px] font-medium text-emerald-600 mt-0.5 m-0">
                                    {totalPaid.toFixed(2)}
                                  </p>
                                </Grid>
                                <Grid item xs={4}>
                                  <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium m-0">Balance</p>
                                  <p className={`text-[11px] font-medium mt-0.5 m-0 ${remainingAmount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    {remainingAmount.toFixed(2)}
                                  </p>
                                </Grid>
                              </Grid>
                            </div>
                          )}
                        </div>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <div className="text-center py-4 text-slate-500 text-[11px] font-medium">
                No invoice details available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const allHeaders = [
  'Ops ID',
  'Request Date',
  'Request Type',
  'Vessel Name',
  'Vessel Code',
  'Cargo Name',
  'Voyage No.',
  'Shipment No.',
  'Invoice No.',
  'Pay order No.',
  'Currency',
  'Amount',
  'Amount Settled',
  'Settlement Date',
  'Accounting Date',
  'Txn Ref Code',
  'Status',
  'Closure Date',
  'Ops PIC',
  'via Company',
  'via Company Code',
];

const initialAmountEntries = [
  { amount: '', date: '' }
];

const transformInvoiceData = (invoiceData) => {
  return invoiceData.map((invoice) => {
    const totalPaid = invoice?.invoiceDetail?.reduce((sum, item) => {
      const itemTotal = item?.paymentEntries?.reduce(
        (a, p) => a + (parseFloat(p?.amount) || 0),
        0
      ) || 0;
      return sum + itemTotal;
    }, 0) || 0;
    const operationDetails = invoice?.opsId || {};
    return {
      _id: invoice._id,
      requestId: invoice._id,
      requestDate: invoice.createdAt,
      requestType: 'Invoice',
      vesselName: invoice.vesselName || '',
      vesselCode: invoice.vesselCode || '',
      opsId: operationDetails?._id || '',
      opsName: operationDetails?.operationName || '',
      cargoName: invoice?.cargoName || '',
      shipmentNumber: invoice.shipmentNumber || '',
      voyageNumber: invoice.voyageNumber || '',
      invoiceNo: invoice.invoiceNo || '',
      payOrderNo: invoice.payOrderNo || '',
      currency: invoice.bankDetails?.currency || 'USD',
      amount: invoice.totals?.finalTotal?.toFixed(2) || 0,
      amountSettled: invoice.totals?.amountSettled || initialAmountEntries,
      amountSettledTotal: totalPaid.toFixed(2),
      settlementDate: invoice.settlementDate || '',
      accountingDate: invoice.createdAt,
      txnRefCode: invoice.txnRefCode || '',
      status: invoice.status || 'Posted',
      closureDate: invoice.closureDate || '',
      opsPIC: invoice.userDetails?.personal?.firstName + ' ' + invoice.userDetails?.personal?.lastName || '',
      viaCompany: invoice.viaCompany || '',
      viaCompanyCode: invoice.viaCompanyCode || '',
    };
  });
};

const Accounting = (props) => {
  const { loader = false, setLoader = () => { } } = props;
  const [loading, setLoading] = useState(false);
  const organizationId = getCurrentUserOrganizationId();
  const [pagination, setPagination] = useState({
    totalPages: 0,
    totalItems: 0,
    offset: 1,
    limit: 50,
    sortBy: "createdAt",
    sortDirection: "desc",
  });
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleHeaders, setVisibleHeaders] = useState(
    allHeaders.reduce((acc, header, index) => {
      acc[header] = index < 8;
      return acc;
    }, {})
  );
  const [viewData, setViewData] = useState({
    isOpen: false,
    data: {}
  });
  const [paymentViewData, setPaymentViewData] = useState({
    isOpen: false,
    data: {}
  });

  const headers = allHeaders.filter(header => visibleHeaders[header]);
  const open = Boolean(anchorEl);

  const [hasFetched, setHasFetched] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const fetchData = async () => {
    if (!hasFetched && (!data || data.length === 0)) {
      setIsInitialLoading(true);
      setLoader(true);
    }

    const response = await getPostedInvoiceByOrganizationId({
      organizationId,
      offset: pagination.offset,
      limit: pagination.limit,
      sortBy: pagination.sortBy,
      sortDirection: pagination.sortDirection
    });
    setRawData(response.data);
    const transformedData = transformInvoiceData(response.data);
    setData(transformedData);
    setPagination((prev) => ({
      ...prev,
      totalPages: response.totalPages,
      totalItems: response.totalItems,
    }));
    setIsInitialLoading(false);
    setLoader(false);
    setHasFetched(true);
  };
  useEffect(() => {
    fetchData();
  }, [pagination.offset, pagination.sortBy, pagination.sortDirection]);

  const handleSortClick = (sortKey) => {
    let newSortDirection = "asc";
    if (pagination.sortBy === sortKey) {
      if (pagination.sortDirection === "asc") newSortDirection = "desc";
      else if (pagination.sortDirection === "desc") {
        newSortDirection = sortKey === "createdAt" ? "asc" : "";
      }
    } else {
      newSortDirection = sortKey === "createdAt" ? "desc" : "asc";
    }

    let nextSortBy = sortKey;
    if (!newSortDirection) {
      nextSortBy = "createdAt";
      newSortDirection = "desc";
    }

    setPagination((prev) => ({
      ...prev,
      offset: 1,
      sortBy: nextSortBy,
      sortDirection: newSortDirection,
    }));
  };


  const onChangePagination = (type, key) => {
    setPagination((prev) => {
      const currentPage = prev.offset || 1;
      const limit = prev.limit || 10;

      let newOffset = currentPage;

      if (type === "first") {
        newOffset = 1;
      } else if (type === "dec" && currentPage > 1) {
        newOffset = currentPage - 1;
      } else if (type === "inc" && currentPage * limit < pagination.totalItems) {
        newOffset = currentPage + 1;
      }

      return {
        ...prev,
        offset: newOffset,
      };
    });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleHeader = (header) => {
    setVisibleHeaders(prev => ({
      ...prev,
      [header]: !prev[header]
    }));
  };

  const handleView = (row) => {
    console.log(row);
    setViewData({
      isOpen: true,
      data: row
    });
  };

  const handlePaymentView = (row) => {
    const paymentData = { ...rawData.find(item => item._id === row._id) };
    delete paymentData.opsId;
    setPaymentViewData({
      isOpen: true,
      data: paymentData
    });
  };

  const handleShowOperation = (opsId) => {
    window.open(`/ops-calc/${opsId}`);
  }

  const handleApprovePayment = async () => {
    try {
      const updatedInvoiceDetail = paymentViewData.data.invoiceDetail.map(item => ({
        ...item,
        paymentEntries: (item.paymentEntries || []).map(entry => ({
          ...entry,
          approved: true,
        }))
      }));
      await updateInvoiceDetails(
        { id: paymentViewData.data._id },
        updatedInvoiceDetail
      );
      fetchData();
      setPaymentViewData({
        isOpen: false,
        data: null
      });
      toastSuccess("Invoice updated successfully");
    } catch (error) {
      console.log(error);
      toastError("Failed to update invoice");
    }
  };

  return (
    <div className="w-full  min-h-[calc(100vh-110px)]">
      <div className="flex justify-start mb-2">
        <div className="relative">
          <button
            onClick={handleClick}
            className="h-7 px-3 bg-[#313C4C] hover:bg-[#0a2a47] text-white text-[11px] font-medium rounded-none flex items-center justify-center gap-1.5 shadow-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Select Columns
          </button>

          {open && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={handleClose}
              />

              {/* Popover */}
              <div className="absolute z-20 mt-1 left-0 bg-white rounded-none shadow-lg border border-gray-300 min-w-48">
                <div className="h-full overflow-y-auto">
                  <div className="space-y-1.5 p-3 h-fit max-h-[50vh] overflow-y-auto scroll-thin">
                    {allHeaders.map(header => (
                      <label
                        key={header}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 text-[11px] font-medium"
                      >
                        <input
                          type="checkbox"
                          checked={visibleHeaders[header]}
                          onChange={() => handleToggleHeader(header)}
                          className="w-3.5 h-3.5 text-[#313C4C] bg-gray-100 border-gray-300 rounded-none focus:ring-[#313C4C]/20"
                        />
                        <span className="text-[11px] font-medium text-gray-700 select-none">
                          {header}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-none border border-slate-300 shadow-sm">
        <TableContainer component={Paper} className="!rounded-none !shadow-none" style={{ scrollbarWidth: 'thin' }}>
          {pagination?.totalItems > pagination?.limit && (
            <CustomPagination
              onChangePagination={onChangePagination}
              filters={pagination}
              totalPage={pagination.totalItems}
            />
          )}
          <Table className="universal-table w-full">
            <TableHead className="bg-[#313C4C]">
              <TableRow>
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
                >
                  S.No
                </TableCell>
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
                >
                  Request ID
                </TableCell>
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap cursor-pointer"
                  onClick={() => handleSortClick("opsName")}
                >
                  Ops Name
                </TableCell>
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
                >
                  Approve Payment
                </TableCell>
                {/* Dynamic headers */}
                {headers.map((header, index) => {
                  const sortMap = {
                    'Shipment No.': 'shipmentNumber',
                    'Voyage No.': 'voyageNumber',
                    'Request Date': 'createdAt',
                  };
                  const sortKey = sortMap[header];
                  return (
                    <TableCell
                      key={header}
                      className={`text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 leading-tight ${sortKey ? 'cursor-pointer' : ''}`}
                      onClick={() => sortKey ? handleSortClick(sortKey) : undefined}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {header}
                        {sortKey && (
                          <div style={{ color: pagination.sortBy === sortKey ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                            {pagination.sortBy === sortKey && pagination.sortDirection === "desc" ? <LuArrowDown size={13} /> : pagination.sortBy === sortKey && pagination.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  )
                })}
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isInitialLoading && (!data || !data.length) ? (
                <TableRow>
                  <TableCell colSpan={headers.length + 5} align="center" className="!bg-white">
                    <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                      {data?.length > 0 && <CircularProgress />}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : data && data.length > 0 ? (
                data.map((row, index) => (
                  <TableRow
                    key={row._id || index}
                    className="h-7 hover:bg-gray-50 cursor-pointer"
                    onDoubleClick={() => handlePaymentView(row)}
                    sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}
                  >
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium text-center border-r border-gray-300">
                      {(pagination.offset - 1) * pagination.limit + index + 1}
                    </TableCell>
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.requestId || '-'}
                    </TableCell>
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      <button onClick={() => handleShowOperation(row.opsId)} className={`text-blue cursor-pointer ${row.opsId ? 'underline' : 'text-gray-400'}`}>
                        {row.opsName || '-'}
                      </button>
                    </TableCell>
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      <button
                        onClick={() => handlePaymentView(row)}
                        className="h-6 px-2 bg-[#313C4C] hover:bg-[#0a2a47] text-white text-[10px] font-medium rounded-none transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                      >
                        View Payments
                      </button>
                    </TableCell>
                    {visibleHeaders['Ops ID'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.opsId || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Request Date'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {moment(row.requestDate).isValid() ? moment(row.requestDate).format('DD-MM-YYYY') : '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Request Type'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.requestType || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Vessel Name'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.vesselName || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Vessel Code'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.vesselCode || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Cargo Name'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.cargoName || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Voyage No.'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.voyageNumber || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Shipment No.'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.shipmentNumber || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Invoice No.'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.invoiceNo || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Pay order No.'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.payOrderNo || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Currency'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.currency || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Amount'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.amount || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Amount Settled'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.amountSettledTotal || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Settlement Date'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {moment(row.settlementDate).isValid() ? moment(row.settlementDate).format('DD-MM-YYYY') : '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Accounting Date'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {moment(row.accountingDate).isValid() ? moment(row.accountingDate).format('DD-MM-YYYY') : '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Txn Ref Code'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.txnRefCode || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Status'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.status || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Closure Date'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {moment(row.closureDate).isValid() ? moment(row.closureDate).format('DD-MM-YYYY') : '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Ops PIC'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.opsPIC || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['via Company'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.viaCompany || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['via Company Code'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.viaCompanyCode || '-'}
                      </TableCell>
                    )}
                    <TableCell className="!py-1 !px-2 text-center border-gray-300">
                      <button
                        onClick={() => handleView(row)}
                        className="p-1 text-[#313C4C] hover:text-[#0a2a47] cursor-pointer"
                        title="View"
                      >
                        <FaEye style={{ fontSize: 14 }} color="#313C4C" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={headers.length + 5} className="!bg-white text-center py-10">
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-2.5">
                        <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <p className="text-[11px] font-medium text-slate-700 mb-0.5">No accounting data available</p>
                      <p className="text-[10px] font-medium text-slate-400">There are no accounting records to display at this time.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {pagination?.totalItems > pagination?.limit && (
            <CustomPagination
              onChangePagination={onChangePagination}
              filters={pagination}
              totalPage={pagination.totalItems}
            />
          )}
        </TableContainer>
      </div>

      <CustomShowModal
        open={viewData.isOpen}
        maxWidth="md"
        title="View Details"
        saveLabel="Save Changes"
        handleClose={() => setViewData({ isOpen: false, data: {} })}
      >
        <AccountingViewModel data={viewData.data} refetch={fetchData} closeModel={() => setViewData({ isOpen: false, data: {} })} />
      </CustomShowModal>
      <CustomAddModal
        open={paymentViewData.isOpen}
        maxWidth="md"
        title="View Payments"
        saveLabel="Approve Payment"
        onSubmitConfirm={() => handleApprovePayment()}
        handleClose={() => setPaymentViewData({ isOpen: false, data: {} })}
      >
        <PaymentViewModal
          data={paymentViewData.data}
          setPaymentViewData={setPaymentViewData}
        />
      </CustomAddModal>

    </div>
  );
};

export default Accounting;